import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SpringIntegrationRefactorContainer } from '~/components/spring-integration-refactor'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import { MuleRefactorContainer } from '~/components/task-forms/MuleRefactorContainer'
import { TaskForm } from '~/components/task/TaskForm'
import { AgentCommand, UsedCommandType, isChatCommand } from '~/constants/commands'
import { useUserSpecificConfig } from '~/contexts/AppConfigContext'
import { useConversationsContext } from '~/contexts/ConversationContext'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import useTaskSubmit from '~/hooks/task/useTaskSubmit'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import LandingPage from '~/layouts/LandingPage'
import { CommandType, IntegrationPlatform, integrationPlatformToJSON } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { getCommandTypeFromUrlName, getCommandUrlName } from '~/utils/urlUtils'
import { getSuperAdminUrl } from '~/utils/utils'
import { SupportButton } from '../common/SupportButton'

/**
 * Component for workspace home page /w/{workspaceUuid}
 * Shows the initial workspace view - LandingPage or GalleryScreen
 * Handles task forms at the workspace level when command params are present
 */
export function WorkspaceHomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const { updateConversation } = useConversationsContext()
  const { handleTaskSubmit } = useTaskSubmit()
  const { setActiveTab, isSearching } = useSidebarContext()
  const { userConfig } = useUserSpecificConfig()
  const isSuperAdmin = userConfig?.is_super_admin || false
  const isPlugin = bridge.isInPlugin()
  const isCompactViewport = useCompactViewport()
  const [chatFocus, setChatFocus] = useState<boolean>()

  useEffect(() => setChatFocus(!isSearching), [isSearching])

  // Helper function to get command from URL params
  const command = useMemo((): UsedCommandType | undefined => {
    const commandParam = searchParams.get('command')
    if (commandParam) {
      const commandType = getCommandTypeFromUrlName(commandParam)
      return commandType ? (commandType as UsedCommandType) : undefined
    }
    return undefined
  }, [searchParams])

  // Helper function to get withExamplePrefilled from URL params
  const withExamplePrefilled = useMemo(() => {
    return searchParams.get('withExamplePrefilled') === 'true'
  }, [searchParams])

  const closeCommand = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('command')
    newParams.delete('withExamplePrefilled')
    newParams.delete('metadata')
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const openCommand = useCallback(
    (
      command: UsedCommandType,
      withExamplePrefilled: boolean,
      metadata?: {
        repositories?: Array<{ conn_type: string; repository: string; branch: string }>
        s3UploadPaths?: string[]
        integrationPlatform?: IntegrationPlatform
      }
    ) => {
      // Read current URL params directly to avoid stale closure issues
      const currentParams = new URLSearchParams(window.location.search)
      const commandUrlName = getCommandUrlName(command)
      if (!commandUrlName) return

      currentParams.set('command', commandUrlName)
      currentParams.set('withExamplePrefilled', String(withExamplePrefilled))

      // Clear existing metadata params
      currentParams.delete('metadata')

      // Encode metadata into URL if provided
      if (metadata) {
        try {
          // Convert enum values to string names for API
          const serializedMetadata = {
            ...metadata,
            integrationPlatform:
              metadata.integrationPlatform !== undefined
                ? integrationPlatformToJSON(metadata.integrationPlatform)
                : undefined
          }
          const metadataJson = JSON.stringify(serializedMetadata)
          currentParams.set('metadata', encodeURIComponent(metadataJson))
        } catch (error) {
          console.error('Failed to encode metadata:', error)
        }
      }

      setSearchParams(currentParams)
    },
    [setSearchParams]
  )

  // Listen for OpenCommand events - pure routing logic
  useEffect(() => {
    const handleOpenCommand = ({
      command,
      withExamplePrefilled = false,
      metadata
    }: {
      command: AgentCommand
      withExamplePrefilled?: boolean
      metadata?: {
        repositories?: Array<{ conn_type: string; repository: string; branch: string }>
        s3UploadPaths?: string[]
        integrationPlatform?: IntegrationPlatform
      }
    }) => {
      // Pure routing - just update URL with command and metadata
      openCommand(command.id as UsedCommandType, withExamplePrefilled, metadata)
    }

    globalEventEmitter.globalOn(
      AppEvents.OpenCommand,
      handleOpenCommand as (...args: unknown[]) => void
    )

    return () => {
      globalEventEmitter.off(AppEvents.OpenCommand, handleOpenCommand)
    }
  }, [openCommand])

  useEffect(() => {
    globalEventEmitter.globalOn(AppEvents.CloseCommand, closeCommand)
    return () => {
      globalEventEmitter.off(AppEvents.CloseCommand, closeCommand)
    }
  }, [closeCommand])

  // Handle task submission from TaskForm
  const handleTaskFormSubmit = useCallback(
    async (taskParams: TaskParams): Promise<boolean> => {
      try {
        // Submit task using the hook
        const taskInfo = await handleTaskSubmit(taskParams)

        if (taskInfo) {
          // Upsert the new conversation in the list (more efficient than refetching all)
          await updateConversation(taskInfo.uuid)

          setActiveTab('tasks')

          // Navigate to the new conversation with task
          const workspacePrefix = currentWorkspace?.uuid ? `/w/${currentWorkspace.uuid}` : ''
          const conversationUrl = `${workspacePrefix}/c/${taskInfo.uuid}`
          navigate(`${conversationUrl}?task_id=${taskInfo.taskId}`)

          return true
        }

        return false
      } catch (error) {
        console.error('[WorkspaceHomePage] Task submission error:', error)
        return false
      }
    },
    [handleTaskSubmit, updateConversation, currentWorkspace, navigate, setActiveTab]
  )

  const taskCommand = useMemo(() => {
    return command && !isChatCommand(command)
  }, [command])

  const home = useMemo(() => {
    return !taskCommand
  }, [taskCommand])

  return (
    <div className='flex flex-col h-full relative'>
      {/* Show task form if command parameter exists */}
      {taskCommand && (
        <div
          className={
            isCompactViewport
              ? 'flex-1 min-h-0 overflow-hidden bg-[#fbfbfb]'
              : 'flex-1 overflow-auto bg-[#fbfbfb]'
          }
        >
          {command === CommandType.MULE_REFACTORING ? (
            <MuleRefactorContainer onClose={closeCommand} onTaskSubmit={handleTaskFormSubmit} />
          ) : command === CommandType.SPRING_INTEGRATION_REFACTORING ? (
            <SpringIntegrationRefactorContainer
              onClose={closeCommand}
              onTaskSubmit={handleTaskFormSubmit}
            />
          ) : (
            <TaskForm
              command={command}
              withExamplePrefilled={withExamplePrefilled}
              closeTask={closeCommand}
              onTaskSubmit={handleTaskFormSubmit}
              className='bg-[#fbfbfb]'
            />
          )}
        </div>
      )}
      {home && (
        <>
          {!isPlugin && (
            <div className='absolute top-5 right-5 z-50 flex items-center gap-3'>
              {isSuperAdmin && (
                <a
                  href={getSuperAdminUrl()}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='px-3 py-1.5 text-sm font-medium text-tokens-primary-2 bg-tokens-primary-3 hover:bg-tokens-status-link-base rounded-lg transition-colors select-none'
                >
                  Super Admin
                </a>
              )}
              <SupportButton />
            </div>
          )}

          <div className='flex-1 overflow-auto'>
            <LandingPage
              onConversationCreated={() => setActiveTab('chats')}
              chatFocus={chatFocus}
              setChatFocus={setChatFocus}
            />
          </div>
        </>
      )}
    </div>
  )
}
